import React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TabPanel from "./TabPanel";
import Divider from "@mui/material/Divider";

const MyTabs = ({ tabs, isVertical, initialTabIndex, id }) => {
  // Filter out dividers to get actual tab count (if needed later)

  const declerativeProps = (index) => ({
    id: `${isVertical ? "vertical" : "horizontal"}-tab-${index}`,
    "aria-controls": `${isVertical ? "vertical" : "horizontal"}-tabpanel-${index}`,
  });

  const getActualTabsCount = () => tabs.filter((t) => t.type !== "divider").length;

  const resolveInitialIndex = () => {
    let idx = undefined;
    if (typeof initialTabIndex === "number") {
      idx = initialTabIndex;
    } else if (id && typeof window !== "undefined") {
      try {
        const raw = localStorage.getItem(`tabs:${id}`);
        const parsed = parseInt(raw, 10);
        if (!isNaN(parsed)) idx = parsed;
      } catch {}
    }
    if (typeof idx !== "number") idx = 0;
    const count = getActualTabsCount();
    if (idx + 1 > count || idx < 0) return 0;

    localStorage.setItem(`tabs:${id}`, String(idx));
    return idx;
  };

  const [tabValue, setTabValue] = React.useState(resolveInitialIndex);
  const [tabKey, setTabKey] = React.useState(resolveInitialIndex);

  // ?tab= ile (aynı route üzerindeyken) sekme değiştirme: initialTabIndex sonradan
  // değişirse (ör. sayfadaki bir link ?tab'i güncellerse) aktif sekmeyi senkronla.
  React.useEffect(() => {
    if (typeof initialTabIndex !== "number") return;
    const count = getActualTabsCount();
    if (initialTabIndex < 0 || initialTabIndex + 1 > count) return;
    setTabValue((prev) => (prev === initialTabIndex ? prev : initialTabIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTabIndex]);

  const handleTabChange = (event, newValue) => {
    if (newValue === tabValue) {
      setTabKey((prev) => prev + 1);
    }
    setTabValue(newValue);
    if (id && typeof window !== "undefined") {
      try {
        localStorage.setItem(`tabs:${id}`, String(newValue));
      } catch {}
    }
  };

  return (
    <div
      style={{
        display: "flex",
        height: isVertical ? "calc(100vh - 4rem)" : "100%",
        flexDirection: isVertical ? "row" : "column",
        marginTop: isVertical ? "0px" : "0px",
      }}
    >
      <Tabs
        orientation={isVertical ? "vertical" : "horizontal"}
        variant="scrollable"
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          display: "flex",
          borderRight: isVertical ? 1 : "none", // vertical tabs have right border, horizontal tabs do not
          borderColor: "divider",
          paddingTop: isVertical ? "20px" : "0px",
          paddingBottom: isVertical ? "20px" : "0px",
          marginTop: isVertical ? "0px" : "0px",
          marginBottom: isVertical ? "0px" : "0px",
          height: isVertical ? "100%" : "auto",

          width: isVertical ? "250px" : "100%",
          backgroundColor: isVertical ? "var(--color-gray-surface)" : "transparent", // light background for vertical tabs
          borderBottom: isVertical ? "none" : "1px solid var(--color-gray-selected)", // border for horizontal tabs
        }}
        TabIndicatorProps={{
          sx: {
            backgroundColor: "var(--color-link)",
            height: isVertical ? "100%" : "3px",
            width: isVertical ? "4px" : undefined, // dikeyse sol çizgi, yataysa alt çizgi
            left: isVertical ? 0 : 0,
            bottom: !isVertical ? 0 : 0,
          },
        }}
      >
        {tabs.map((tab, index) => {
          // Check if this is a divider
          if (tab.type === "divider") {
            return (
              <Divider
                key={index + "divider"}
                orientation={isVertical ? "horizontal" : "vertical"}
                sx={{
                  margin: isVertical ? "8px 16px" : "0 8px",
                  backgroundColor: "var(--color-gray-selected)",
                }}
              />
            );
          }

          // Calculate the actual tab index (excluding dividers)
          const actualTabIndex = tabs.slice(0, index).filter((t) => t.type !== "divider").length;

          return (
            <Tab
              key={index + "tab"}
              label={tab.label}
              sx={{
                textTransform: "none",
                justifyContent: isVertical ? "center" : "flex-start", // align text to the left
                alignItems: isVertical ? "flex-start" : "center", // align vertically left too
                textAlign: "left", // ensure text is left-aligned
                width: "100%", // use full width to allow alignment
                height: isVertical ? "100%" : "auto",
                pl: isVertical ? 4 : 0,
                //paddingTop: isVertical && index === 0 ? "30px" : undefined, // add padding to the first tab if vertical
                //fontWeight: "bold",
                fontWeight: "normal",
                marginRight: isVertical ? "0px" : "10px", // right margin for horizontal tabs

                border: isVertical ? "none" : "1px solid var(--color-gray-selected)", // border for horizontal tabs
                borderBottom: "none", // no bottom border for vertical tabs
                fontSize: "16px",
                color: "var(--color-solid)",
                //backgroundColor: isVertical ? undefined : "var(--color-gray-hover)", // Onemli su anda horizontal tablerde burası basılabilir anlamı gelmiyor selected olmayanlar icin
                "&:hover": {
                  backgroundColor: "var(--color-gray-hover)", // hover effect
                },
                "&.Mui-selected": {
                  // fontWeight: 600,
                  color: "var(--color-primary-surface)", // selected tab color
                  backgroundColor: "var(--color-gray-selected)", // selected tab background color
                },
                transition: "all 0.2s ease-in-out", // 👈 burada
              }}
              {...declerativeProps(actualTabIndex)}
              onClick={() => {
                if (tabValue === actualTabIndex) {
                  handleTabChange(null, actualTabIndex);
                }
              }} // toggle tab
            />
          );
        })}
      </Tabs>
      {tabs.map((tab, index) => {
        // Skip divider for TabPanel
        if (tab.type === "divider") {
          return null;
        }

        // Calculate the actual tab index (excluding dividers)
        const actualTabIndex = tabs.slice(0, index).filter((t) => t.type !== "divider").length;

        return (
          <TabPanel
            key={index + "tabpanel" + tabKey}
            value={tabValue}
            index={actualTabIndex}
            style={{
              padding: isVertical ? "0px" : "0px",
              borderRadius: isVertical ? "0px" : "0px",
              width: "100%",
              paddingTop: isVertical ? "0px" : "20px",
              height: "100%",
            }}
          >
            {/* <div style={{ padding: "10px" }}>{tab.content}</div> */}
            {tab.content}
          </TabPanel>
        );
      })}
    </div>
  );
};

export default MyTabs;
